import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useRef, useCallback, useEffect, useMemo } from 'react';

const createEvent = (input)=>{
    return (providerEventMetadata)=>{
        const eventMetadata = {
            ...providerEventMetadata,
            ...input.metadata,
            id: crypto.randomUUID(),
            time: new Date().toISOString()
        };
        return {
            metadata: eventMetadata,
            payload: input.createPayload(eventMetadata)
        };
    };
};

const EVENT_TYPES = {
    ACTION: "ACTION",
    PAGE_VIEW: "PAGE_VIEW"
};

const TrackingProviderContext = createContext(null);
const useTrackingProvider = ()=>{
    const context = useContext(TrackingProviderContext);
    if (context === null) {
        throw new Error("`useTrackingProvider` must be used within a `TrackingProvider`");
    }
    return context;
};

const TrackingProvider = ({ brand, children, hasUserConsent })=>{
    const pluginRegistryRef = useRef(new Map());
    const registerPlugin = useCallback((plugin)=>{
        const pluginId = plugin.id;
        const registryItem = pluginRegistryRef.current.get(plugin.id) ?? {
            plugin,
            referenceCount: 0,
            isLoaded: false
        };
        const canHandleLoading = !plugin.isUserConsentRequired || hasUserConsent;
        // Load plugin only if user consent is required and not provided (will be done later on user consent change)
        if (!registryItem.isLoaded && canHandleLoading) {
            const loadPlugin = getLoadPlugin(plugin);
            loadPlugin();
            registryItem.isLoaded = true;
        }
        registryItem.referenceCount++;
        pluginRegistryRef.current.set(pluginId, registryItem);
    }, [
        hasUserConsent
    ]);
    const unregisterPlugin = useCallback((plugin)=>{
        const pluginId = plugin.id;
        const registryItem = pluginRegistryRef.current.get(pluginId);
        if (!registryItem) return;
        registryItem.referenceCount--;
        if (registryItem.referenceCount > 0) return;
        if (registryItem.isLoaded) {
            getUnloadPlugin(plugin)();
        }
        pluginRegistryRef.current.delete(pluginId);
    }, []);
    // User consent change management
    useEffect(()=>{
        pluginRegistryRef.current.forEach((registryItem)=>{
            const { plugin } = registryItem;
            if (!plugin.isUserConsentRequired) return;
            const isCurrentlyLoaded = registryItem.isLoaded;
            if (hasUserConsent && !isCurrentlyLoaded) {
                getLoadPlugin(plugin)();
                registryItem.isLoaded = true;
            } else if (!hasUserConsent && isCurrentlyLoaded) {
                getUnloadPlugin(plugin)();
                registryItem.isLoaded = false;
            }
        });
    }, [
        hasUserConsent
    ]);
    // Dedicated `useEffect` to handle the cleanup phase (on TrackingProvider unmounting) that prevents any potential memory leak on the consumer side by removing any plugin reference.
    useEffect(()=>{
        const currentRegistry = pluginRegistryRef.current;
        return ()=>{
            currentRegistry.forEach(({ plugin })=>{
                getUnloadPlugin(plugin)();
                currentRegistry.delete(plugin.id);
            });
        };
    }, []);
    const contextValue = useMemo(()=>({
            brand,
            hasUserConsent,
            registerPlugin,
            unregisterPlugin
        }), [
        brand,
        hasUserConsent,
        registerPlugin,
        unregisterPlugin
    ]);
    return /*#__PURE__*/ jsx(TrackingProviderContext.Provider, {
        value: contextValue,
        children: children
    });
};
const getLoadPlugin = (plugin)=>{
    return withErrorDecorator(plugin.load, {
        id: plugin.id,
        methodName: "load"
    });
};
const getUnloadPlugin = (plugin)=>{
    return withErrorDecorator(plugin.unload, {
        id: plugin.id,
        methodName: "unload"
    });
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withErrorDecorator = (target, { id, methodName })=>{
    return (...input)=>{
        try {
            return target(...input);
        } catch (error) {
            console.error(`Error ${methodName}ing plugin ${id}:`, error);
        }
    };
};

const TrackingScopeContext = createContext(null);
const useTrackingScope = ()=>{
    const context = useContext(TrackingScopeContext);
    if (context === null) {
        throw new Error("`useTrackingScope` must be used within a `TrackingScope`");
    }
    return context;
};

const TrackingScope = ({ component, plugins, children })=>{
    const { registerPlugin, unregisterPlugin } = useTrackingProvider();
    const initialPluginsRef = useRef(plugins);
    useEffect(()=>{
        const plugins = initialPluginsRef.current;
        // Subscribe to plugins (this will increment reference count)
        plugins.forEach((plugin)=>{
            registerPlugin(plugin);
        });
        return ()=>{
            // Unsubscribe from plugins (this will decrement reference count)
            // Plugins will only be unloaded when reference count reaches 0
            plugins.forEach((plugin)=>{
                unregisterPlugin(plugin);
            });
        };
    }, [
        registerPlugin,
        unregisterPlugin
    ]);
    const contextValue = useMemo(()=>({
            component,
            plugins: initialPluginsRef.current
        }), [
        component
    ]);
    return /*#__PURE__*/ jsx(TrackingScopeContext.Provider, {
        value: contextValue,
        children: children
    });
};

const logger = {
    error: (message)=>{
        console.error(message);
    }
};

const useEventFactory = ({ eventType, hookName })=>{
    const { brand } = useTrackingProvider();
    const { component, plugins } = useTrackingScope();
    return useCallback((input)=>{
        (Array.isArray(input) ? input : [
            input
        ]).forEach((event)=>{
            const eventEnvelope = event({
                brand,
                component
            });
            if (eventEnvelope.metadata.type !== eventType) {
                logger.error(`You cannot use \`${hookName}\` with an event type not equal to \`${eventType}\`.`);
                return;
            }
            if (eventEnvelope.metadata.pluginId) {
                const plugin = plugins.find((plugin)=>plugin.id === eventEnvelope.metadata.pluginId);
                if (plugin) {
                    plugin.send({
                        ...eventEnvelope.payload
                    });
                }
            }
        });
    }, [
        brand,
        component,
        eventType,
        hookName,
        plugins
    ]);
};

/**
 * Hook for tracking action events.
 *
 * @returns A callback function that, when invoked, will dispatch all associated action events
 */ const useActionEvent = ()=>{
    return useEventFactory({
        eventType: EVENT_TYPES.ACTION,
        hookName: "useActionEvent"
    });
};

/**
 * A hook for tracking pageView events.
 *
 * @param path - The current path of the page being viewed (it can also be an identifier if the current view has no dedicated path)
 * @param events - A function or array of functions that create page view events
 * @returns void - Events are automatically dispatched when path changes
 */ const usePageViewEvent = (path, events)=>{
    const eventFactory = useEventFactory({
        eventType: EVENT_TYPES.PAGE_VIEW,
        hookName: "usePageViewEvent"
    });
    /**
	 * Ref is used to set and retrieve the latest value without impacting the React rendering lifecycle.
	 * It avoids extra hook dependency that could lead to unwanted duplicate event submission
	 */ const eventFactoryRef = useRef(eventFactory);
    const eventsRef = useRef(events);
    useEffect(()=>{
        eventFactoryRef.current = eventFactory;
    }, [
        eventFactory
    ]);
    useEffect(()=>{
        eventsRef.current = events;
    }, [
        events
    ]);
    useEffect(()=>{
        eventFactoryRef.current(eventsRef.current);
    }, [
        path
    ]);
};

const createPlugin = (input)=>{
    // Dumb factory for now to allow more check/sharing later.
    return input;
};

export { TrackingProvider, TrackingScope, createEvent, createPlugin, useActionEvent, usePageViewEvent };
